import { Component, OnDestroy, OnInit, input, output, signal, effect } from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'wire-rule-editor',
  templateUrl: './rule-editor.component.html',
  styleUrls: ['./rule-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule],
})
export class RuleEditorComponent implements OnInit, OnDestroy {
  // Signal-based inputs
  editorContent = input<string>('');
  title = input<string>('SETTINGS.WORKFLOWS.RULES.EDITOR.TITLE');
  description = input<string>('SETTINGS.WORKFLOWS.RULES.EDITOR.DESCRIPTION');
  
  // Signal-based outputs
  contentChange = output<string>();
  validChange = output<boolean>();
  errorChange = output<string[]>();
  
  // Internal state
  #editorContent = signal('');
  #destroy$ = new Subject<void>();

  // Form control for editor content
  editorFormControl = new FormControl('');
  
  readonly operatorKeywords = ['EXIST', 'NOT_EXIST', 'EQUALS', 'CHANGED', 'CHANGED_FROM_TO'];

  constructor(private readonly translateService: TranslateService) {
    // Effect to handle input changes
    effect(() => {
      const value = this.editorContent();
      this.#editorContent.set(value);
      this.editorFormControl.setValue(value, { emitEvent: false });
      
      if (value) {
        // Use setTimeout to ensure we don't validate during change detection
        setTimeout(() => this.#validateContent(value), 0);
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to form control value changes instead of manually handling input events
    this.editorFormControl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.#destroy$)
      )
      .subscribe(content => {
        if (content !== null) {
          this.#editorContent.set(content);
          this.contentChange.emit(content);
          this.#validateContent(content);
        }
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  // Getter for template to access the current editor content (not needed with reactive forms)
  get displayContent(): string {
    return this.#editorContent();
  }

  #validateContent(content: string): void {
    const errors: string[] = [];
    
    if (!content?.trim()) {
      errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.EMPTY'));
      this.errorChange.emit(errors);
      this.validChange.emit(false);
      return;
    }
    
    // Use case-insensitive matching for keywords
    const upperContent = content.toUpperCase();
    
    // Check for basic structure
    if (!upperContent.includes('IF ')) {
      errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.NO_IF'));
    }
    
    // Check for action syntax
    if (!upperContent.includes(' DO ')) {
      errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.NO_ACTION'));
    }
    
    // Check for incorrect "ELSE IF" usage (should be "ELSEIF")
    if (upperContent.includes('ELSE IF')) {
      errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.ELSE_IF_SPACE'));
    }
    
    // Check that actions follow proper syntax
    const lines = content.split('\n');
    
    for (const line of lines) {
      if (!line || line.trim() === '') continue;
      
      const upperLine = line.toUpperCase().trim();
      
      // Skip comment lines
      if (upperLine.startsWith('//') || upperLine.startsWith('#')) continue;
      
      // Check if line starts with IF, ELSEIF, or ELSE but doesn't have DO
      if ((upperLine.startsWith('IF ') || upperLine.startsWith('ELSEIF ') || upperLine.startsWith('ELSE ')) 
          && !upperLine.includes(' DO ')) {
        errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.MISSING_DO'));
        continue;
      }
      
      const doIndex = upperLine.indexOf(' DO ');
      if (doIndex === -1) continue;
      
      const actionPart = upperLine.substring(doIndex + 4).trim();
      
      if (actionPart.startsWith('SEND_MESSAGE')) {
        if (!actionPart.includes('WITH')) {
          errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.SEND_MESSAGE_NO_WITH'));
        } else {
          // Case-insensitive regex match for WITH pattern
          const withMatch = line.match(/with\s+(['"].*?['"])/i);
          if (!withMatch) {
            errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.SEND_MESSAGE_NO_TEMPLATE'));
          }
        }
      } else if (!actionPart.startsWith('NOTIFY_PARTICIPANTS')) {
        errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.INVALID_ACTION'));
      }
      
      // Check condition syntax using case-insensitive checks
      if (upperLine.startsWith('IF ') || upperLine.startsWith('ELSEIF ')) {
        // Check for valid operators
        const conditionPart = upperLine.substring(0, doIndex);
        
        const hasOperator = this.operatorKeywords.some(op => 
          conditionPart.includes(` ${op} `)
        );
        
        if (!hasOperator) {
          errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.MISSING_OPERATOR'));
        }
        
        // Check for EQUALS operator syntax
        if (conditionPart.includes(' EQUALS ')) {
          // Case-insensitive regex match for EQUALS pattern
          const equalsMatch = line.match(/equals\s+(['"].*?['"])/i);
          if (!equalsMatch) {
            errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.EQUALS_REQUIRES_VALUE'));
          }
        }
        
        // Check for CHANGED_FROM_TO operator syntax
        if (conditionPart.includes(' CHANGED_FROM_TO ')) {
          // Case-insensitive regex match for CHANGED_FROM_TO pattern
          const fromToMatch = line.match(/changed_from_to\s+(['"].*?['"])\s+(['"].*?['"])/i);
          if (!fromToMatch) {
            errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.CHANGED_FROM_TO_REQUIRES_TWO_VALUES'));
          }
        }
      }
    }
    
    // Check for balanced parentheses
    let parenCount = 0;
    for (const char of content) {
      if (char === '(') parenCount++;
      else if (char === ')') parenCount--;
      
      if (parenCount < 0) {
        errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.UNBALANCED_PARENTHESES'));
        break;
      }
    }
    
    if (parenCount !== 0) {
      errors.push(this.translateService.instant('SETTINGS.WORKFLOWS.RULES.EDITOR.ERRORS.UNBALANCED_PARENTHESES'));
    }
    
    console.log('Rule validation:', content ? content.substring(0, 30) + '...' : 'empty', 'Errors:', errors.length);
    this.errorChange.emit(errors);
    this.validChange.emit(errors.length === 0);
  }
}
